// app/javascript/compcalc/components/LifePension.jsx

import React from "react";
import PropTypes from "prop-types";
import { withStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import MenuIcon from '@material-ui/icons/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import withErrorHandling from './ErrorHandling';
import { currencyFormatter } from '../utils';


const styles = theme => ({
  root: {
    flexGrow: 1,
    marginTop: theme.spacing(1),
  },
  wrapContent: {
    display: 'inline-block',
  },
  textField: {
    width: 100,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  textFieldAmount: {
    width: 150,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  alignCenter: {
    // background: 'green',
    display: 'flex',
    justifyContent: 'center',    // Horizontal align.
    alignItems: 'center',        // Vertical align.
  },
  alignLeft: {
    // background: 'blue',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
  },
  alignRight: {
    // background: 'red',
    display: 'flex',
    flexFlow: 'row-reverse nowrap',
    alignItems: 'center',
  },
  paperStretch: {
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    display: 'inline-block',
  },
  pre: {
    width: '100%',
    maxWidth: '800px',
    whiteSpace: 'pre-wrap',
  }
});


class LifePension extends React.PureComponent {

  constructor() {
    super();

    let dates = [];
    for (let i = 1996; i <= 2018; ++i)
      dates.push(i);
    this.dates = dates;

    this.itemsPercentages = [];
    for (let i = 70; i < 100; ++i) {
      this.itemsPercentages.push(
        <MenuItem dense key={'percent' + i} value={i}>{i}%</MenuItem>
      );
    }

    this.state = {
      disability: 85,
      pension: [0,0,0,0],
    };
  }

  componentDidMount = () => {
    this.fetchLP();
  }

  fetchLP = () => {
    let url = `api/v1/life-pension.json?disability=${this.state.disability}`;
    this.props.fetch(url, results => {
      // console.log(results)
      this.setState({
        pension: results['pension'],
      });
    }).catch(e => this.props.showError(e, 'Error obtaining life pension from server'));
  }

  onSelectPD = (event) => {
    // console.log(event.target.name, event.target.value);
    this.setState({ [event.target.name]: event.target.value }, () => { this.fetchLP() }); // Alternative componentDidUpdate().
  };

  render() {
    const { classes } = this.props;

    return (
      <Grid container spacing={1} className={classes.root} justifyContent="center">
        <Grid item xs={12} className={classes.alignCenter}>
          {/* Permanent Disability */}
          <Paper className={classes.paper}>
            <FormControl>
              <FormLabel component="legend">Permanent Disability</FormLabel>
              <Select
                value={ this.state.disability }
                onChange={this.onSelectPD}
                input={<Input id="disability" name="disability" />}
                autoWidth
              >
                { this.itemsPercentages }
              </Select>
            </FormControl>
          </Paper>
        </Grid>

        <Grid item xs={12} className={classes.alignCenter}>
          <Paper className={classes.paperStretch}>
            <FormLabel component="legend">Date of Injury (on or after)</FormLabel>
          </Paper>
        </Grid>

        <Grid item xs={3} className={classes.alignCenter}>
          {/* 07/01/1994 */}
          <Paper className={classes.paper}>
            <TextField
              id="year94"
              label="07/01/1994"
              value={currencyFormatter.format(this.state.pension[0])}
              className={classes.textFieldAmount}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={3} className={classes.alignCenter}>
          {/* 07/01/1995 */}
          <Paper className={classes.paper}>
            <TextField
              id="year95"
              label="07/01/1995"
              value={currencyFormatter.format(this.state.pension[1])}
              className={classes.textFieldAmount}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={3} className={classes.alignCenter}>
          {/* 07/01/1996 */}
          <Paper className={classes.paper}>
            <TextField
              id="year96"
              label="07/01/1996"
              value={currencyFormatter.format(this.state.pension[2])}
              className={classes.textFieldAmount}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={3} className={classes.alignCenter}>
          {/* 01/01/2006 */}
          <Paper className={classes.paper}>
            <TextField
              id="year06"
              label="01/01/2006"
              value={currencyFormatter.format(this.state.pension[3])}
              className={classes.textFieldAmount}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <div className={classes.pre}>
              <Typography variant="body1" dangerouslySetInnerHTML={{ __html: lpText }} />
            </div>
          </Paper>
        </Grid>

      </Grid>
    );
  }

};

export default withStyles(styles)(withErrorHandling(LifePension));

const lpText = `When the permanent disability is 70% or greater, life pension weekly payments are made using the following formula:
<b>LIFE PENSION = (PD%-60) x 00.015 x WeeklyEarningWithinMaximum. LC 4659.</b>

There have been no increases in maximum weekly earnings since 1/1/2006.

Date life pension payments start is not reflected because life pension payments do not commence until permanent partial payment end. For a date of injury in 2003 with a permanent partial disability of 70% life pension payments would not start until 426.5 weeks after the permanent and stationary date. Payments would not begin until 8.17 years after the P&S date.
For disability on or after 1/1/2003 the rate of payment is not known until the start of payments due to COLA increases.

"For injuries occurring on or after Jan 1, 2003, an employee who becomes entitled to receive a life pension or total permanent disability indemnity... shall have that payment increased annually commencing on January 1, 2004, and each January 1 thereafter, by an amount ...". LC 4659(c).`;

